import React, {
    createContext,
    memo,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { Box, IconButton, Paper, Typography, alpha, useTheme } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import { Indicator } from '../../../proto/generated/indicator'
import { Condition_CompositeCondition_Operator } from '../../../proto/generated/nexus'
import { getUserAlertMessage, type AlertMessageContext } from '../../../util/alerts'
import {
    CompositeBranchNode,
    ConditionForestModel,
    countConditionRowsInForest,
    removeNodeAtPath,
    showsSplitRailForMultiRootRoot,
    toggleNodeParentToGroupOp,
    toggleRootSiblingOp
} from '../../../util/alertConditionTree'
import { NonInteractiveTooltip } from '../../custom'
import { useStyles } from './styles'
import BinIcon from '../../../assets/icons/BinIcon'

function opButtonLabel(op: Condition_CompositeCondition_Operator): 'AND' | 'OR' {
    return op === Condition_CompositeCondition_Operator.OR ? 'OR' : 'AND'
}

function operatorTooltipTitle(op: Condition_CompositeCondition_Operator): string {
    return op === Condition_CompositeCondition_Operator.OR
        ? 'Currently matching any condition (OR)'
        : 'Currently matching all conditions (AND)'
}

type OperatorPalette = {
    rail: string
    pillBg: string
    pillBorder: string
    pillText: string
}

function useOperatorPalette(op: Condition_CompositeCondition_Operator): OperatorPalette {
    const theme = useTheme()
    const isOr = op === Condition_CompositeCondition_Operator.OR
    const dark = theme.palette.mode === 'dark'
    return useMemo(() => {
        if (isOr) {
            return dark
                ? {
                      rail: '#F57C00',
                      pillBg: 'rgba(230, 81, 0, 0.35)',
                      pillBorder: '#FF9800',
                      pillText: '#FFE0B2'
                  }
                : {
                      rail: '#FFB74D',
                      pillBg: '#FFF3E0',
                      pillBorder: '#FFCC80',
                      pillText: '#E65100'
                  }
        }
        return dark
            ? {
                  rail: '#42A5F5',
                  pillBg: 'rgba(21, 101, 192, 0.45)',
                  pillBorder: '#64B5F6',
                  pillText: '#E3F2FD'
              }
            : {
                  rail: '#64B5F6',
                  pillBg: '#E3F2FD',
                  pillBorder: '#90CAF9',
                  pillText: '#1565C0'
              }
    }, [dark, isOr])
}

/** How horizontal rails stop so they don’t cross a nested group’s vertical spine. */
type TreeRowRailMode = 'multiRoot' | 'nestedUnderCondition'

/** Rail = split (pair row with new condition). Footer + = add sibling in the same group. */
export type TreeRowConnectorAdd = {
    onClick: () => void
}

type TreeOperatorGroupProps = {
    op: Condition_CompositeCondition_Operator
    onClick: () => void
    rows: React.ReactNode[]
    rowKeys: string[]
    rowRailMode: TreeRowRailMode
    /** When true, rows are only child subtrees (no parent “head” row); first row is not the nested-group head stub. */
    omitHeadRow?: boolean
    /** Per-row control on the horizontal rail (undefined = no chip on that row). */
    rowConnectorAdds?: Array<TreeRowConnectorAdd | undefined | null>
    spineFooterAdd?: { onClick: () => void }
}

const SPINE_PX = 32
const SPINE_HALF = SPINE_PX / 2
/** Left edge of horizontal segments (meets parent vertical rail). */
const H_LINE_LEFT_PX = -(SPINE_HALF + 1)
/**
 * Leaf row: segment ends here (grey bar starts); avoids drawing through nested spines.
 * + chip is centered on this segment — keep enough span so the chip clears the AND/OR pill on the spine.
 */
const LEAF_LINE_END_X = 46
const CONNECTOR_ROW_CONTENT_INSET_PX = LEAF_LINE_END_X
const LEAF_H_LINE_WIDTH_PX = LEAF_LINE_END_X - H_LINE_LEFT_PX
const LEAF_CONNECTOR_BTN_SIZE_PX = 22
/** Leaf “+” sits toward the condition (end of horizontal rail), not centered on the stub. */
const LEAF_CONNECTOR_BTN_CENTER_X = LEAF_LINE_END_X - LEAF_CONNECTOR_BTN_SIZE_PX / 2 - 10
/** Vertical anchor for T-junctions on short rows (leaf rail chip, parent head stub). */
const ROW_CONNECTOR_TOP_PX = SPINE_HALF

/**
 * Nested `TreeOperatorGroup` reports AND/OR pill center Y (px from its grid top) so the parent row’s
 * horizontal stub can align to the operator, not the spine midpoint.
 */
const OperatorPillCenterReportContext = createContext<((centerYFromNestedGridTop: number) => void) | null>(null)

function TreeOperatorGroup({
    op,
    onClick,
    rows,
    rowKeys,
    rowRailMode,
    omitHeadRow = false,
    rowConnectorAdds,
    spineFooterAdd
}: TreeOperatorGroupProps) {
    const c = useOperatorPalette(op)
    const label = opButtonLabel(op)
    const n = rows.length
    const spineHalf = SPINE_HALF
    const hasFooter = Boolean(spineFooterAdd)

    const [nestedPillCenterYByRow, setNestedPillCenterYByRow] = useState<Record<number, number>>({})
    const reportNestedPillForRow = useCallback((rowIndex: number) => (centerY: number) => {
        setNestedPillCenterYByRow((prev) => {
            const prevY = prev[rowIndex]
            if (prevY !== undefined && Math.abs(prevY - centerY) < 0.5) {
                return prev
            }
            return { ...prev, [rowIndex]: centerY }
        })
    }, [])

    const reportPillToParentRow = useContext(OperatorPillCenterReportContext)
    const reportPillToParentRowRef = useRef(reportPillToParentRow)
    reportPillToParentRowRef.current = reportPillToParentRow

    const gridRef = useRef<HTMLDivElement | null>(null)
    const spineRef = useRef<HTMLDivElement | null>(null)
    const firstRowRef = useRef<HTMLDivElement | null>(null)
    const lastRowRef = useRef<HTMLDivElement | null>(null)
    const footerBtnRef = useRef<HTMLButtonElement | null>(null)
    const [vMetrics, setVMetrics] = useState<{
        lineTop: number
        lineHeight: number
        pillTop: number
    } | null>(null)

    const measureSpine = useCallback(() => {
        const grid = gridRef.current
        const spine = spineRef.current
        const first = firstRowRef.current
        const last = lastRowRef.current
        if (!grid || !spine || !first || !last || n < 1) {
            setVMetrics(null)
            return
        }
        const gr = grid.getBoundingClientRect()
        const sr = spine.getBoundingClientRect()
        const fb = first.getBoundingClientRect()
        const lb = last.getBoundingClientRect()
        /** Y at spine for each row’s horizontal stub (must match row rail `hLineTop`). */
        const rowConnectorY = (rowTop: number, rowHeight: number, rowIndex: number) => {
            const reported = nestedPillCenterYByRow[rowIndex]
            if (reported !== undefined) {
                return rowTop + reported
            }
            return rowTop + Math.min(SPINE_HALF, rowHeight / 2)
        }
        const yFirstConnectorAbs = rowConnectorY(fb.top, fb.height, 0)
        // Vertical rail: start exactly at first T-junction (not fb.top+16px on tall rows — avoids open-ended segment above nested groups).
        const lastRowTailAbs = lb.bottom - Math.min(SPINE_HALF, lb.height / 2)
        let yCloseLineAbs = lastRowTailAbs
        if (hasFooter && footerBtnRef.current) {
            const br = footerBtnRef.current.getBoundingClientRect()
            yCloseLineAbs = br.top + br.height / 2
        }
        const y1 = yFirstConnectorAbs - gr.top
        const yEnd = yCloseLineAbs - gr.top
        const bleedTop = 1
        const bleedBottom = hasFooter ? 0 : 1
        const lineTop = y1 - bleedTop
        const lineHeight = Math.max(0, yEnd - y1 + bleedTop + bleedBottom)
        // AND/OR pill: center on the drawn spine from first T-junction to last row’s tail — not the midpoint
        // of the first/last *row connector* Y (both sit near row tops), or the pill sits inside a tall middle
        // row after splitting a child into a nested group.
        const pillCenterAbs = (yFirstConnectorAbs + lastRowTailAbs) / 2
        const pillTop = pillCenterAbs - sr.top
        const pillCenterFromGridTop = pillCenterAbs - gr.top
        if (Number.isFinite(pillCenterFromGridTop)) {
            reportPillToParentRowRef.current?.(pillCenterFromGridTop)
        }
        setVMetrics({ lineTop, lineHeight, pillTop })
    }, [n, hasFooter, nestedPillCenterYByRow])

    useLayoutEffect(() => {
        measureSpine()
        const grid = gridRef.current
        if (!grid) return undefined

        const ro = new ResizeObserver(() => measureSpine())
        ro.observe(grid)
        const first = firstRowRef.current
        const last = lastRowRef.current
        if (first) ro.observe(first)
        if (last && last !== first) ro.observe(last)
        const spine = spineRef.current
        if (spine) ro.observe(spine)
        const footerBtn = footerBtnRef.current
        if (hasFooter && footerBtn) ro.observe(footerBtn)

        window.addEventListener('resize', measureSpine)
        const id = window.requestAnimationFrame(() => measureSpine())
        const id2 = window.requestAnimationFrame(() => measureSpine())
        return () => {
            ro.disconnect()
            window.removeEventListener('resize', measureSpine)
            window.cancelAnimationFrame(id)
            window.cancelAnimationFrame(id2)
        }
    }, [measureSpine, n, hasFooter])

    const handleFooterAddClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            spineFooterAdd?.onClick()
        },
        [spineFooterAdd]
    )

    const handleSpineClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            onClick()
        },
        [onClick]
    )

    const pillTopPx =
        vMetrics === null
            ? null
            : vMetrics.lineHeight > 0
              ? vMetrics.pillTop
              : vMetrics.lineTop

    return (
        <Box
            ref={gridRef}
            sx={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: `${SPINE_PX}px minmax(0, 1fr)`,
                rowGap: (t) => t.spacing(0.8),
                alignItems: 'center',
                width: '100%'
            }}
        >
            {vMetrics !== null && vMetrics.lineHeight > 0 && (
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        left: SPINE_PX / 2 - 1,
                        top: vMetrics.lineTop,
                        width: 2,
                        height: vMetrics.lineHeight,
                        bgcolor: c.rail,
                        borderRadius: 0,
                        zIndex: 0,
                        pointerEvents: 'none'
                    }}
                />
            )}
            <Box
                ref={spineRef}
                role="button"
                tabIndex={0}
                aria-label={`Toggle logical operator, currently ${label}`}
                onClick={handleSpineClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        onClick()
                    }
                }}
                sx={{
                    gridColumn: 1,
                    gridRow: n > 0 ? `1 / ${n + 1}` : '1',
                    position: 'relative',
                    width: SPINE_PX,
                    minHeight: n > 0 ? 48 : 32,
                    cursor: 'pointer',
                    outline: 'none',
                    alignSelf: 'stretch',
                    justifySelf: 'stretch',
                    '&:focus-visible': {
                        boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}`,
                        borderRadius: 1
                    }
                }}
            >
                <NonInteractiveTooltip title={operatorTooltipTitle(op)} arrow placement="top">
                    <Paper
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            ...(pillTopPx === null
                                ? { top: '50%', transform: 'translate(-50%, -50%)' }
                                : { top: pillTopPx, transform: 'translate(-50%, -50%)' }),
                            px: 0.25,
                            py: 0.25,
                            borderRadius: 999,
                            zIndex: 3,
                            userSelect: 'none',
                            pointerEvents: 'auto'
                        }}
                    >
                        <Box sx={{
                            px: 0.75,
                            py: 0.35,
                            borderRadius: 999,
                            bgcolor: c.pillBg,
                            color: c.pillText,
                            fontSize: 10,
                            fontWeight: 500,
                            letterSpacing: 0.2,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                        }}>
                            {label}
                        </Box>
                    </Paper>
                </NonInteractiveTooltip>
            </Box>
            {hasFooter && spineFooterAdd && (
                <>
                    <Box
                        sx={{
                            gridColumn: 1,
                            gridRow: n + 1,
                            position: 'relative',
                            width: SPINE_PX,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 0.5,
                            minHeight: 36,
                            flexShrink: 0
                        }}
                    >
                        <Paper sx={{
                            zIndex: 2,
                            width: 28,
                            height: 28,
                            padding: '2px',
                            borderRadius: '100%',
                            transform: 'translateY(-10px)',
                        }}>
                            <NonInteractiveTooltip title="Add condition" arrow placement="top">
                                <IconButton
                                    ref={footerBtnRef}
                                    size="small"
                                    aria-label="Add condition"
                                    onClick={handleFooterAddClick}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        padding: 0.25,
                                        bgcolor: c.pillBg,
                                        color: c.pillText,
                                        '&:hover': {
                                            bgcolor: c.pillBg,
                                            opacity: 0.92
                                        }
                                    }}
                                >
                                    <AddIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </NonInteractiveTooltip>
                        </Paper>
                    </Box>
                    <Box sx={{ gridColumn: 2, gridRow: n + 1, minHeight: 36 }} />
                </>
            )}
            {rows.map((row, i) => {
                const connector = rowConnectorAdds?.[i]
                const onConnectorAdd = connector?.onClick
                const isLeafRail = Boolean(onConnectorAdd)
                /** Parent “head” row: no nested spine in this cell — stop at content inset, not at child spine center. */
                const isNestedGroupHead =
                    rowRailMode === 'nestedUnderCondition' && i === 0 && !isLeafRail && !omitHeadRow
                /** Nested subtree: align to child AND/OR pill when reported; else row center. */
                const reportedPillY = nestedPillCenterYByRow[i]
                const hLineTop =
                    isLeafRail || isNestedGroupHead
                        ? ROW_CONNECTOR_TOP_PX
                        : reportedPillY !== undefined
                          ? `${reportedPillY}px`
                          : '50%'
                const hLineSx = isLeafRail
                    ? { width: `${LEAF_H_LINE_WIDTH_PX}px` }
                    : isNestedGroupHead
                      ? { width: (t) => `calc(${t.spacing(0.5)} + ${SPINE_HALF + 1}px)` }
                      : { width: (t) => `calc(${t.spacing(0.5)} + ${SPINE_PX + 1}px)` }
                return (
                    <Box
                        key={rowKeys[i] ?? `row-${i}`}
                        ref={(el) => {
                            const rowEl = el as HTMLDivElement | null
                            if (i === 0) firstRowRef.current = rowEl
                            if (i === n - 1) lastRowRef.current = rowEl
                        }}
                        sx={{
                            gridColumn: 2,
                            gridRow: i + 1,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'flex-start',
                            minWidth: 0,
                            pl: onConnectorAdd ? `${CONNECTOR_ROW_CONTENT_INSET_PX}px` : (t) => t.spacing(0.5)
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                left: H_LINE_LEFT_PX,
                                top: hLineTop,
                                transform: 'translateY(-50%)',
                                height: 2,
                                bgcolor: c.rail,
                                borderRadius: 0,
                                pointerEvents: 'none',
                                zIndex: 0,
                                ...hLineSx
                            }}
                        />
                        {onConnectorAdd && (
                            <Paper sx={{
                                position: 'absolute',
                                left: LEAF_CONNECTOR_BTN_CENTER_X,
                                top: ROW_CONNECTOR_TOP_PX,
                                transform: 'translate(-50%, -50%)',
                                zIndex: 2,
                                width: 28,
                                height: 28,
                                padding: '2px',
                                borderRadius: '100%'
                            }}>
                                <NonInteractiveTooltip
                                    title="Add nested condition"
                                    arrow
                                    placement="top"
                                >
                                    <IconButton
                                        size="small"
                                        aria-label="Split: pair with a new condition"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onConnectorAdd()
                                        }}
                                        sx={{
                                            bgcolor: c.pillBg,
                                            color: c.pillText,
                                            '&:hover': {
                                                bgcolor: c.pillBg,
                                                opacity: 0.92
                                            }
                                        }}
                                    >
                                        <CallSplitIcon sx={{ fontSize: 14, transform: 'rotate(90deg)' }} />
                                    </IconButton>
                                </NonInteractiveTooltip>
                            </Paper>
                        )}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                position: 'relative',
                                zIndex: 1,
                                isolation: 'isolate'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <OperatorPillCenterReportContext.Provider value={reportNestedPillForRow(i)}>
                                {row}
                            </OperatorPillCenterReportContext.Provider>
                        </Box>
                    </Box>
                )
            })}
        </Box>
    )
}

export type OnForestChange = (
    nextOrUpdater: ConditionForestModel | ((prev: ConditionForestModel) => ConditionForestModel)
) => void

export type CompositeConditionAddIntent = 'siblingUnderGroup' | 'nestedUnderBranchHead'

type BranchProps = {
    node: CompositeBranchNode
    path: number[]
    onForestChange: OnForestChange
    onAddChildRequest: (path: number[], intent?: CompositeConditionAddIntent) => void
    onEditConditionRequest: (path: number[]) => void
    symbolsDisplayValue: string
    indicators: Indicator[]
    messageContext?: AlertMessageContext
    depth: number
    deletable: boolean
    groupOp: Condition_CompositeCondition_Operator
    /** When true, this row sits under a tree spine; add control is drawn on the rail (outside the grey box). */
    showAddOnConnector?: boolean
}

const CompositeBranchBlock = memo(function CompositeBranchBlock({
    node,
    path,
    onForestChange,
    onAddChildRequest,
    onEditConditionRequest,
    symbolsDisplayValue,
    indicators,
    messageContext,
    depth,
    deletable,
    groupOp,
    showAddOnConnector = false
}: BranchProps) {
    const { classes } = useStyles()
    const theme = useTheme()
    const effectiveGroupOp =
        node.children.length > 0 ? node.parentToGroupOp : groupOp
    const palette = useOperatorPalette(effectiveGroupOp)
    const actionIconButtonSx = useMemo(
        () => ({
            color: palette.pillText,
            borderRadius: 1,
            '&:hover': {
                bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.22 : 0.1)
            },
            '&:active': {
                bgcolor: alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.32 : 0.16)
            },
            '&.Mui-disabled': {
                color: palette.pillText,
                opacity: 0.38
            }
        }),
        [palette.pillText, theme]
    )
    const [conditionLabel, setConditionLabel] = useState('')
    useEffect(() => {
        let cancelled = false
        void (async () => {
            const label = await getUserAlertMessage(node.security, symbolsDisplayValue, indicators, messageContext)
            if (!cancelled) setConditionLabel(label)
        })()
        return () => {
            cancelled = true
        }
    }, [node.security, symbolsDisplayValue, indicators, messageContext])

    const onRemove = useCallback(() => {
        onForestChange((f) => removeNodeAtPath(f, path))
    }, [onForestChange, path])

    const onToggleParentToGroup = useCallback(() => {
        onForestChange((f) => toggleNodeParentToGroupOp(f, path))
    }, [onForestChange, path])

    const onAddNestedFromRow = useCallback(() => {
        onAddChildRequest(path, 'nestedUnderBranchHead')
    }, [onAddChildRequest, path])

    const onRowClick = useCallback(() => {
        onEditConditionRequest(path)
    }, [onEditConditionRequest, path])

    const useConnectorAddPlacement = node.children.length > 0 || showAddOnConnector

    const conditionRow = (
        <Box
            className={classes.compositeConditionRow}
            sx={{
                bgcolor: palette.pillBg,
                color: palette.pillText,
                border: `1px solid ${palette.pillBorder}`
            }}
        >
            <Box
                onClick={onRowClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onRowClick()
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Edit condition: ${conditionLabel}`}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: -2 }
                }}
            >
                <Typography
                    component="span"
                    className={classes.compositeConditionText}
                    title={conditionLabel}
                >
                    {conditionLabel}
                </Typography>
            </Box>
            <Box
                className={classes.compositeConditionActions}
                sx={{ borderLeftColor: palette.pillBorder }}
            >
                {!useConnectorAddPlacement && (
                    <NonInteractiveTooltip
                        title="Add nested condition"
                        arrow
                        placement="top"
                    >
                        <IconButton
                            size="small"
                            aria-label="Split: pair with a new condition"
                            sx={actionIconButtonSx}
                            onClick={(e) => {
                                e.stopPropagation()
                                onAddNestedFromRow()
                            }}
                        >
                            <CallSplitIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                        </IconButton>
                    </NonInteractiveTooltip>
                )}
                <NonInteractiveTooltip title="Remove condition" arrow placement="top">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Remove condition"
                            sx={actionIconButtonSx}
                            onClick={(e) => {
                                e.stopPropagation()
                                onRemove()
                            }}
                            disabled={!deletable}
                        >
                            <BinIcon fontSize="small" />
                        </IconButton>
                    </span>
                </NonInteractiveTooltip>
            </Box>
        </Box>
    )

    if (node.children.length === 0) {
        return conditionRow
    }

    const hideHeadRow = node.compositeUi?.hideHeadRow === true
    const childBlocks = node.children.map((child, i) => (
        <CompositeBranchBlock
            key={`${path.join('-')}-${i}`}
            node={child}
            path={[...path, i]}
            onForestChange={onForestChange}
            onAddChildRequest={onAddChildRequest}
            onEditConditionRequest={onEditConditionRequest}
            symbolsDisplayValue={symbolsDisplayValue}
            indicators={indicators}
            messageContext={messageContext}
            depth={depth + 1}
            deletable={deletable}
            groupOp={node.parentToGroupOp}
            showAddOnConnector
        />
    ))
    const childConnectorAdds = node.children.map((child, i) =>
        child.children.length === 0
            ? { onClick: () => onAddChildRequest([...path, i], 'nestedUnderBranchHead') }
            : undefined
    )

    if (hideHeadRow) {
        return (
            <TreeOperatorGroup
                op={node.parentToGroupOp}
                onClick={onToggleParentToGroup}
                rowRailMode="nestedUnderCondition"
                omitHeadRow
                rowKeys={node.children.map((_, i) => `${path.join('-')}-c-${i}`)}
                rowConnectorAdds={childConnectorAdds}
                rows={childBlocks}
                spineFooterAdd={{ onClick: () => onAddChildRequest(path, 'siblingUnderGroup') }}
            />
        )
    }

    return (
        <TreeOperatorGroup
            op={node.parentToGroupOp}
            onClick={onToggleParentToGroup}
            rowRailMode="nestedUnderCondition"
            rowKeys={[`${path.join('-')}-head`, ...node.children.map((_, i) => `${path.join('-')}-c-${i}`)]}
            rowConnectorAdds={[
                { onClick: () => onAddChildRequest(path, 'nestedUnderBranchHead') },
                ...childConnectorAdds
            ]}
            rows={[conditionRow, ...childBlocks]}
            spineFooterAdd={{ onClick: () => onAddChildRequest(path, 'siblingUnderGroup') }}
        />
    )
})

export type CompositeConditionTreeProps = {
    forest: ConditionForestModel
    onForestChange: OnForestChange
    onAddChildRequest: (path: number[], intent?: CompositeConditionAddIntent) => void
    onEditConditionRequest: (path: number[]) => void
    symbolsDisplayValue: string
    indicators: Indicator[]
    messageContext?: AlertMessageContext
}

export const CompositeConditionTree = memo(function CompositeConditionTree({
    forest,
    onForestChange,
    onAddChildRequest,
    onEditConditionRequest,
    symbolsDisplayValue,
    indicators,
    messageContext,
}: CompositeConditionTreeProps) {
    const onToggleRootSibling = useCallback(() => {
        onForestChange((f) => toggleRootSiblingOp(f))
    }, [onForestChange])

    const deletable = countConditionRowsInForest(forest) > 1

    if (forest.roots.length === 0) {
        return null
    }

    if (forest.roots.length === 1) {
        return (
            <CompositeBranchBlock
                node={forest.roots[0]}
                path={[0]}
                onForestChange={onForestChange}
                onAddChildRequest={onAddChildRequest}
                onEditConditionRequest={onEditConditionRequest}
                symbolsDisplayValue={symbolsDisplayValue}
                indicators={indicators}
                messageContext={messageContext}
                depth={0}
                deletable={deletable}
                groupOp={forest.rootSiblingOp}
            />
        )
    }

    return (
        <TreeOperatorGroup
            op={forest.rootSiblingOp}
            onClick={onToggleRootSibling}
            rowRailMode="multiRoot"
            rowKeys={forest.roots.map((_, i) => `root-${i}`)}
            rowConnectorAdds={forest.roots.map((root, i) =>
                showsSplitRailForMultiRootRoot(root)
                    ? { onClick: () => onAddChildRequest([i], 'nestedUnderBranchHead') }
                    : undefined
            )}
            rows={forest.roots.map((root, i) => (
                <CompositeBranchBlock
                    key={`root-${i}`}
                    node={root}
                    path={[i]}
                    onForestChange={onForestChange}
                    onAddChildRequest={onAddChildRequest}
                    onEditConditionRequest={onEditConditionRequest}
                    symbolsDisplayValue={symbolsDisplayValue}
                    indicators={indicators}
                    messageContext={messageContext}
                    depth={0}
                    deletable={deletable}
                    groupOp={forest.rootSiblingOp}
                    showAddOnConnector
                />
            ))}
            spineFooterAdd={{ onClick: () => onAddChildRequest([]) }}
        />
    )
})
